// @spec GHG-STR-055
'use client';

/**
 * Internal Verification Workflow - detail page.
 * Shows verification request with checklist and findings.
 */

import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { useVerificationDetail } from '@/features/ghg/api/use-verification';
import { VerificationDetail } from '@/features/ghg/components';
import i18n from '@/lib/i18n';

export const Route = createFileRoute(
  '/_authenticated/ghg/verification/internal/$verificationId',
)({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.verificationDetail'),
  }),
  component: VerificationDetailPage,
});

function VerificationDetailPage() {
  const { t } = useTranslation('ghg');
  const { verificationId } = Route.useParams();
  const { verification, isLoading, isError, error, refetch } =

    useVerificationDetail(verificationId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-4xl">
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-6 text-center">
            <p className="font-medium text-destructive">{t('verification.failedToLoad')}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {error instanceof Error ? error.message : t('verification.unknownError')}
            </p>
            <Link to="/ghg/verification/internal" className="mt-4 inline-block">
              <Button variant="outline">{t('verification.backToRequests')}</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!verification) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-lg font-medium">{t('verification.requestNotFound')}</p>
          <Link to="/ghg/verification/internal" className="mt-4 inline-block">
            <Button variant="outline">{t('verification.backToRequests')}</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Back button */}
        <div className="flex items-center gap-4">
          <Link to="/ghg/verification/internal">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <h1 className="text-2xl font-semibold">{t('verification.verificationDetail')}</h1>
        </div>

        {/* Verification detail */}
        <VerificationDetail
          verification={verification}
          onRefresh={() => void refetch()}
        />
      </div>
    </div>
  );
}
